import { Form, Modal } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { lossReasonRules } from "../constants/loss-reason.ts"
import { TextArea } from "../../../components/Input/TextArea.tsx"

interface MoveLeadToLostFormData {
  lost_reason: string
}

interface MoveLeadToLostModalProps {
  leadName?: string | null
  open: boolean
  onCancel: () => void
  onConfirm: (lostReason: string) => Promise<void>
}

export function MoveLeadToLostModal({
  leadName,
  open,
  onCancel,
  onConfirm
}: MoveLeadToLostModalProps) {
  const { t } = useTranslation()
  const saving = useAppSelector((state) => state.leads.saveFetching)
  const [form] = Form.useForm<MoveLeadToLostFormData>()

  useEffect(() => {
    if (!open) form.resetFields()
  }, [form, open])

  const submit = async () => {
    let values: MoveLeadToLostFormData
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    try {
      await onConfirm(values.lost_reason.trim())
    } catch {
      // The thunk reports the refusal; swallowed only to keep it handled.
    }
  }

  return (
    <Modal
      open={open}
      title={t("leadArchive.moveToLostTitle", {
        name: leadName?.trim() || t("leadArchive.thisLead")
      })}
      okText={t("leadArchive.moveToLostConfirm")}
      cancelText={t("leadArchive.cancel")}
      okButtonProps={{ danger: true }}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      keyboard={!saving}
      maskClosable={false}
      onOk={submit}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" disabled={saving}>
        <Form.Item<MoveLeadToLostFormData>
          name="lost_reason"
          label={t("leadArchive.lossReason")}
          className="mb-0"
          rules={lossReasonRules(t("leadArchive.lossReasonRequired"))}
        >
          <TextArea
            rows={3}
            placeholder={t("leadArchive.lossReasonPlaceholder")}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
